import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Menu,
  MenuItem,
  Popover,
  Snackbar,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material"
import {
  GridFilterItem,
  GridFilterModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useSearchParams } from "react-router-dom"

import { useAppDispatch, useAppSelector } from "@app/hooks"
import { CampusClient } from "@clients/campusClient"
import GenericDialog from "@cmp/genericDialog"
import { LoadingComponent } from "@cmp/loaders/loadingComponent"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import { Role, User } from "@features/login/loginSlice"
import { showSuccessSnackbar, SnackbarDuration } from "@features/ui/uiSlice"
import { ManageProgramsDialog } from "./manageProgramsDialog"
import StudentActionPopups from "../../programs/program/students/studentActionPopups"
import { StudentsTableGrid } from "./students-grid/studentsTable.grid"
import { buildStudentsTableColumns } from "./students-grid/columns/studentsTable.columns"
import {
  controlButtonSx,
  ExportControl,
} from "./students-grid/filters/students.table.toolbar"
import {
  selectStudentsDataProgramStatus,
  selectStudentsTableData,
} from "@features/students-tab/studentsTab.selectors"
import {
  fetchStudentsData,
  ProgramStatus,
} from "@features/students-tab/studentsTab.slice"
import AppIcon from "@utils/appIcon"
import appIcons from "@utils/appIcons"
import usePrevious from "@utils/hooks/usePrevious"
import { StudentAcademicStatusDTO } from "./studentAcademicStatus.dto"
import { AttentionSignalKey, getAttentionSignals } from "./studentFlags"
import {
  FilterFieldPicker,
  PickedPredicate,
  SIGNAL_PREDICATES,
} from "./filterFieldPicker"
import { BuiltInPill, SavedViewsBar } from "./savedViewsBar"
import {
  buildBuiltInViews,
  effectiveFilterItems,
  filterModelsEquivalent,
  isBuiltInViewId,
  isEffectiveFilterItem,
  loadViews,
  paramsEqual,
  SavedView,
  saveViews,
  ViewParams,
} from "./savedViews"

// A "pure mentor" holds the mentor role and none of the staff-wide ones.
// Mixed-role staff (admins/PMs who also hold the mentor role) are not pure —
// their job is the whole cohort. One definition drives both the my-students
// landing view and the role-aware column defaults (Mentor column hidden:
// under mentor-is-me it would always read "me").
export function isPureMentor(user: User | null): boolean {
  return (
    (user?.roles.includes(Role.Mentor) ?? false) &&
    !user?.roles.some((role) =>
      [
        Role.LmsAdmin,
        Role.SchoolMasterAdmin,
        Role.ProgramManager,
        Role.AccountManager,
      ].includes(role),
    )
  )
}

// The one chip the strip renders everything as: a label, an optional remove,
// an optional click (the Status chip opens its scope menu). One grammar for
// status, attention, mentor-is-me and column predicates alike.
type StripChip = {
  key: string
  label: string
  onRemove?: () => void
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

// The students page after the views-are-filters convergence: ONE views row
// (the old status tabs, the attention pill and the saved views merged into
// one pill grammar) over ONE chip strip (the selected view's whole
// definition, editable in place). Search is a transient lookup lens on its
// own row; the grid renders whatever the chips mean.
export function StudentsTabView() {
  const dispatch = useAppDispatch()
  const data = useAppSelector(selectStudentsTableData)
  const dataProgramStatus = useAppSelector(selectStudentsDataProgramStatus)
  const user = useAppSelector(selectLoggedInUser)
  const userClientId = user?.clientId ?? null

  const [manageProgramsFor, setManageProgramsFor] = useState<{
    id: string
    name: string
  } | null>(null)
  // The whole view state lives in the URL: tab, search, scope toggles and the
  // expanded student — so any view can be shared or restored by pasting a link.
  const [searchParams, setSearchParams] = useSearchParams()
  const urlTab = searchParams.get("tab")
  const [selectedProgramStatus, setSelectedProgramStatus] =
    useState<ProgramStatus>(
      urlTab === "upcoming" || urlTab === "ended" ? urlTab : "active",
    )
  const [flaggedOnly, setFlaggedOnly] = useState(
    searchParams.get("flagged") === "1",
  )
  // Optional single-signal cut inside the flagged scope; URL-mirrored so a
  // "students stalled right now" link is shareable like everything else.
  const [signalFilter, setSignalFilter] = useState<AttentionSignalKey | null>(
    () => {
      const fromUrl = searchParams.get("signal")
      return (
        SIGNAL_PREDICATES.find((signal) => signal.key === fromUrl)?.key ?? null
      )
    },
  )
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") ?? "")
  const expandedStudentId = searchParams.get("student")
  // Pure mentors land on "My students" (their built-in view); anyone can
  // widen with one pill click. An empty "my roster" would blank the page for
  // mixed-role staff (same guard the calendar defaults use).
  const pureMentor = isPureMentor(user)
  const hasMentorRole = user?.roles.includes(Role.Mentor) ?? false
  const [myStudentsOnly, setMyStudentsOnly] = useState(
    searchParams.get("mine") === "1",
  )
  const defaultedUserRef = useRef<string | null>(null)
  useEffect(() => {
    // A URL that explicitly sets the scope wins over the mentor default.
    if (searchParams.get("mine") !== null) {
      defaultedUserRef.current = user?.clientId ?? null
      return
    }
    if (user && defaultedUserRef.current !== user.clientId) {
      defaultedUserRef.current = user.clientId
      setMyStudentsOnly(pureMentor)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pureMentor])

  // Mirror every view-state change into the URL (replace, not push — browsing
  // the grid must not pollute history).
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    const setOrDelete = (key: string, value: string | null) => {
      if (value === null || value === "") next.delete(key)
      else next.set(key, value)
    }
    setOrDelete(
      "tab",
      selectedProgramStatus === "active" ? null : selectedProgramStatus,
    )
    setOrDelete("q", searchQuery || null)
    setOrDelete("flagged", flaggedOnly ? "1" : null)
    setOrDelete("signal", flaggedOnly ? signalFilter : null)
    setOrDelete("mine", myStudentsOnly ? "1" : null)
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedProgramStatus,
    searchQuery,
    flaggedOnly,
    signalFilter,
    myStudentsOnly,
  ])
  // The canonical view-state param set — the exact keys the URL mirror
  // writes. Pill matching and drift run on this (minus q, which is a lens,
  // not scope), so the pills and the address bar can never disagree.
  const currentViewParams = useMemo<ViewParams>(() => {
    const params: ViewParams = {}
    if (selectedProgramStatus !== "active") params.tab = selectedProgramStatus
    if (searchQuery) params.q = searchQuery
    if (flaggedOnly) params.flagged = "1"
    if (flaggedOnly && signalFilter) params.signal = signalFilter
    if (myStudentsOnly) params.mine = "1"
    return params
  }, [
    selectedProgramStatus,
    searchQuery,
    flaggedOnly,
    signalFilter,
    myStudentsOnly,
  ])

  // The grid's column filter, mirrored up so the chip strip can render it and
  // views can carry it. Applying a view hands the stored model back down
  // through a one-shot ref: the grid consumes it after its (re)mount, which
  // survives the remount a status switch triggers.
  const [currentGridFilter, setCurrentGridFilter] =
    useState<GridFilterModel | null>(null)
  const pendingGridFilterRef = useRef<GridFilterModel | null | undefined>(
    undefined,
  )
  const [gridFilterSeq, setGridFilterSeq] = useState(0)
  const consumePendingGridFilter = useCallback(() => {
    const pending = pendingGridFilterRef.current
    pendingGridFilterRef.current = undefined
    return pending
  }, [])

  // The grid's api lives with the page: the [+ Filter] picker, the chip strip
  // and the Export control all drive the grid from the row directly above it.
  const gridApiRef = useGridApiRef()
  // Applied column filters as chips: only items that actually cut rows count
  // (a half-built condition with no value yet is not a predicate). Labels
  // resolve fields to their column headers.
  const columnHeaderNames = useMemo(() => {
    const names = new Map<string, string>()
    for (const column of buildStudentsTableColumns(selectedProgramStatus)) {
      if (column.headerName) names.set(column.field, column.headerName)
    }
    return names
  }, [selectedProgramStatus])
  const activeFilterItems = useMemo(
    () => (currentGridFilter?.items ?? []).filter(isEffectiveFilterItem),
    [currentGridFilter],
  )
  const filterChipLabel = useCallback(
    (item: GridFilterItem) => {
      const name = columnHeaderNames.get(item.field) ?? item.field
      const operator = FILTER_OPERATOR_LABELS[item.operator] ?? item.operator
      const value = Array.isArray(item.value)
        ? item.value.join(", ")
        : item.value
      return value === undefined || value === null || value === ""
        ? `${name} ${operator}`
        : `${name} ${operator} ${value}`
    },
    [columnHeaderNames],
  )
  const removeFilterItem = useCallback(
    (target: GridFilterItem) => {
      if (
        !currentGridFilter ||
        typeof gridApiRef.current?.setFilterModel !== "function"
      ) {
        return
      }
      gridApiRef.current.setFilterModel({
        ...currentGridFilter,
        items: currentGridFilter.items.filter((item) => item !== target),
      })
    },
    [currentGridFilter, gridApiRef],
  )

  // ---- saved views: the page owns the state, the bar only draws pills ----
  const [views, setViews] = useState<SavedView[]>([])
  useEffect(() => {
    if (userClientId) {
      setViews(loadViews(userClientId))
    }
  }, [userClientId])
  const persistViews = (next: SavedView[]) => {
    setViews(next)
    if (userClientId) {
      saveViews(userClientId, next)
    }
  }
  const builtIns = useMemo(
    () => buildBuiltInViews(hasMentorRole),
    [hasMentorRole],
  )

  // Active = last applied or definition-equal view; exactly one or zero
  // pills. Built-ins match on params AND an empty column filter (they are
  // exact definitions — a filtered subset under an "Active · 675" pill would
  // make the label lie). Saved views match on params + filter equivalence and
  // stay sticky when the definition drifts — that stickiness is what the
  // drift dot and the inline Save · Save as new · Reset report.
  const [activeViewId, setActiveViewId] = useState<string | null>(null)
  useEffect(() => {
    // A just-applied view's grid filter is still in flight to the grid; the
    // grid reports the model back after consuming it and this effect re-runs
    // with settled state.
    if (pendingGridFilterRef.current !== undefined) return
    const gridFilterEmpty = effectiveFilterItems(currentGridFilter).length === 0
    const builtInMatch = gridFilterEmpty
      ? builtIns.find((view) => paramsEqual(view.params, currentViewParams))
      : undefined
    const savedMatch = views.find(
      (view) =>
        paramsEqual(view.params, currentViewParams) &&
        filterModelsEquivalent(view.gridFilter, currentGridFilter),
    )
    const match = builtInMatch ?? savedMatch
    setActiveViewId((current) => {
      if (match) {
        if (current === match.id) return current
        // A saved view that duplicates a built-in's definition stays selected
        // when it is the one the user applied.
        if (current !== null && !isBuiltInViewId(current)) {
          const currentView = views.find((view) => view.id === current)
          if (
            currentView &&
            paramsEqual(currentView.params, currentViewParams) &&
            filterModelsEquivalent(currentView.gridFilter, currentGridFilter)
          ) {
            return current
          }
        }
        return match.id
      }
      if (current === null || isBuiltInViewId(current)) return null
      return views.some((view) => view.id === current) ? current : null
    })
  }, [currentViewParams, currentGridFilter, views, builtIns])

  const activeSavedView = views.find((view) => view.id === activeViewId)
  const isDrifted =
    activeSavedView !== undefined &&
    !(
      paramsEqual(activeSavedView.params, currentViewParams) &&
      filterModelsEquivalent(activeSavedView.gridFilter, currentGridFilter)
    )

  // Applying a view replaces the WHOLE view-state param set — absent keys
  // reset to defaults, exactly like pasting the view's link. The one
  // exception is search: it is a transient lookup lens, so applying a view
  // leaves a live query alone (legacy views that saved a q still restore it).
  const applyView = (
    params: ViewParams,
    gridFilter: GridFilterModel | null,
  ) => {
    const tab =
      params.tab === "upcoming" || params.tab === "ended"
        ? params.tab
        : "active"
    const flagged = params.flagged === "1" && tab === "active"
    setSelectedProgramStatus(tab)
    if (params.q !== undefined) setSearchQuery(params.q)
    setFlaggedOnly(flagged)
    setSignalFilter(
      flagged
        ? SIGNAL_PREDICATES.find((signal) => signal.key === params.signal)
            ?.key ?? null
        : null,
    )
    setMyStudentsOnly(params.mine === "1")
    // The applied view wins over the mentor default, same as an explicit URL.
    defaultedUserRef.current = user?.clientId ?? null
    pendingGridFilterRef.current = gridFilter
    setGridFilterSeq((seq) => seq + 1)
  }

  const selectView = (viewId: string) => {
    const builtIn = builtIns.find((view) => view.id === viewId)
    if (builtIn) {
      setActiveViewId(builtIn.id)
      applyView(builtIn.params, null)
      return
    }
    const saved = views.find((view) => view.id === viewId)
    if (saved) {
      setActiveViewId(saved.id)
      applyView(saved.params, saved.gridFilter)
    }
  }

  // What a save captures: the current chips, whole. Search stays out (a lens,
  // not scope); an empty column filter stores as null so definition equality
  // stays clean.
  const savedParamsSnapshot = (): ViewParams => {
    const params = { ...currentViewParams }
    delete params.q
    return params
  }
  const savedGridFilterSnapshot = (): GridFilterModel | null =>
    activeFilterItems.length > 0 ? currentGridFilter : null

  // ---- save / rename dialog (shared by + New view, Save as new, Rename) ----
  const [viewDialog, setViewDialog] = useState<{
    kind: "save" | "rename"
    viewId?: string
    name: string
  } | null>(null)
  const trimmedDialogName = viewDialog?.name.trim() ?? ""
  const nameTaken =
    viewDialog !== null &&
    trimmedDialogName !== "" &&
    (builtIns.some((view) => view.name === trimmedDialogName) ||
      views.some(
        (view) =>
          view.name === trimmedDialogName && view.id !== viewDialog.viewId,
      ))
  const canSubmitDialog =
    viewDialog !== null && trimmedDialogName !== "" && !nameTaken
  const submitViewDialog = () => {
    if (viewDialog === null || !canSubmitDialog) return
    const now = new Date().toISOString()
    if (viewDialog.kind === "save") {
      const view: SavedView = {
        id: crypto.randomUUID(),
        name: trimmedDialogName,
        params: savedParamsSnapshot(),
        gridFilter: savedGridFilterSnapshot(),
        createdAt: now,
        updatedAt: now,
      }
      persistViews([...views, view])
      setActiveViewId(view.id)
      dispatch(showSuccessSnackbar("View saved", SnackbarDuration.short))
    } else {
      persistViews(
        views.map((view) =>
          view.id === viewDialog.viewId
            ? { ...view, name: trimmedDialogName, updatedAt: now }
            : view,
        ),
      )
    }
    setViewDialog(null)
  }

  // ---- drift actions (inline on the chip strip, not in a kebab) ----
  const updateActiveView = () => {
    if (!activeSavedView) return
    const now = new Date().toISOString()
    persistViews(
      views.map((view) =>
        view.id === activeSavedView.id
          ? {
              ...view,
              params: savedParamsSnapshot(),
              gridFilter: savedGridFilterSnapshot(),
              updatedAt: now,
            }
          : view,
      ),
    )
    dispatch(showSuccessSnackbar("View updated", SnackbarDuration.short))
  }
  const resetActiveView = () => {
    if (!activeSavedView) return
    applyView(activeSavedView.params, activeSavedView.gridFilter)
  }

  // Delete is immediate with an undo snackbar (no modal): views are cheap,
  // local and re-creatable, and undo restores the exact object.
  const [deletedView, setDeletedView] = useState<{
    view: SavedView
    index: number
  } | null>(null)
  const deleteView = (viewId: string) => {
    const index = views.findIndex((view) => view.id === viewId)
    if (index < 0) return
    setDeletedView({ view: views[index], index })
    persistViews(views.filter((view) => view.id !== viewId))
  }
  const undoDeleteView = () => {
    if (deletedView === null) return
    const next = [...views]
    next.splice(Math.min(deletedView.index, next.length), 0, deletedView.view)
    persistViews(next)
    setDeletedView(null)
  }

  // Per-status row counts: preloaded from the backend on mount, then kept in
  // sync by each status's own load — the pill never guesses.
  const [tabCounts, setTabCounts] = useState<
    Partial<Record<ProgramStatus, number>>
  >({})
  useEffect(() => {
    let cancelled = false
    CampusClient.get<Record<ProgramStatus, number>>(
      "student-academic-status-dashboard/search-counts",
    )
      .then((counts) => {
        if (!cancelled) {
          // Loaded statuses already hold the authoritative count; only fill gaps.
          setTabCounts((current) => ({ ...counts, ...current }))
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // While a query is live, pill labels switch from totals to match counts so
  // a zero on the current view is never ambiguous — the student may simply
  // live on another view. Matching runs against the data model on the backend.
  const [searchMatchCounts, setSearchMatchCounts] = useState<Record<
    ProgramStatus,
    number
  > | null>(null)
  useEffect(() => {
    const query = searchQuery.trim()
    if (query === "") {
      setSearchMatchCounts(null)
      return
    }
    let cancelled = false
    const handle = window.setTimeout(() => {
      CampusClient.get<Record<ProgramStatus, number>>(
        `student-academic-status-dashboard/search-counts?q=${encodeURIComponent(
          query,
        )}`,
      )
        .then((counts) => {
          if (!cancelled) setSearchMatchCounts(counts)
        })
        .catch(() => {
          if (!cancelled) setSearchMatchCounts(null)
        })
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [searchQuery])

  const prevSelectedProgramStatus = usePrevious(selectedProgramStatus)
  useEffect(() => {
    if (data === "idle") {
      dispatch(fetchStudentsData(selectedProgramStatus))
    } else if (selectedProgramStatus !== prevSelectedProgramStatus) {
      dispatch(fetchStudentsData(selectedProgramStatus))
    }
  }, [data, selectedProgramStatus, prevSelectedProgramStatus, dispatch])

  const isLoading = data === "pending"
  const isDataReady = typeof data !== "string"

  useEffect(() => {
    // Attribute the count to the status the rows BELONG to, not the one that
    // is currently selected — on a view switch this effect re-runs one render
    // before the pending state lands, and the old view's rows must not be
    // counted against the new view's label.
    if (isDataReady && dataProgramStatus) {
      setTabCounts((counts) =>
        counts[dataProgramStatus] === data.length
          ? counts
          : { ...counts, [dataProgramStatus]: data.length },
      )
    }
  }, [isDataReady, data, dataProgramStatus])

  // Search runs against the data model (name, email, HubSpot id, class) —
  // the same fields the backend counts — never against rendered columns, so
  // a hidden email column still matches. Shared by the visible rows and the
  // attention/my-students match counts.
  const searchTokens = useMemo(
    () => searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [searchQuery],
  )
  const rowMatchesSearch = useCallback(
    (row: StudentAcademicStatusDTO) => {
      const haystacks = [
        row.contactDetails?.name,
        row.contactDetails?.email,
        row.contactDetails?.hubspotContactId,
        row.registrationDetails?.className,
        ...(row.additionalClassNames ?? []),
      ]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.toLowerCase())
      return searchTokens.every((token) =>
        haystacks.some((haystack) => haystack.includes(token)),
      )
    },
    [searchTokens],
  )

  // Last-known Active-scope counts for the Needs-attention and My-students
  // pills: attributed the same way tabCounts is — only when the loaded rows
  // ARE the active status's — and kept while browsing other views, so the
  // pills never blank on a view switch.
  const [activeScopeCounts, setActiveScopeCounts] = useState<{
    flagged: number
    mine: number
  } | null>(null)
  useEffect(() => {
    if (!isDataReady || dataProgramStatus !== "active") return
    let flagged = 0
    let mine = 0
    for (const row of data) {
      if (getAttentionSignals(row).length > 0) flagged += 1
      if (row.staff?.mentor?.userId === user?.clientId) mine += 1
    }
    setActiveScopeCounts((current) =>
      current?.flagged === flagged && current?.mine === mine
        ? current
        : { flagged, mine },
    )
  }, [isDataReady, data, dataProgramStatus, user])

  // While a query is live the two data-derived pills switch to match counts
  // too (computable only while the active rows are the loaded ones — "…"
  // otherwise, in the fixed slot).
  const searchScopeCounts = useMemo(() => {
    if (
      searchTokens.length === 0 ||
      !isDataReady ||
      dataProgramStatus !== "active"
    ) {
      return null
    }
    let flagged = 0
    let mine = 0
    for (const row of data) {
      if (!rowMatchesSearch(row)) continue
      if (getAttentionSignals(row).length > 0) flagged += 1
      if (row.staff?.mentor?.userId === user?.clientId) mine += 1
    }
    return { flagged, mine }
  }, [
    searchTokens,
    isDataReady,
    dataProgramStatus,
    data,
    rowMatchesSearch,
    user,
  ])

  const builtInPills: BuiltInPill[] = builtIns
    .map((view) => {
      const status = BUILT_IN_STATUS[view.id]
      if (status) {
        return {
          id: view.id,
          name: view.name,
          count: searchMatchCounts
            ? searchMatchCounts[status]
            : tabCounts[status] ?? null,
          totalCount: tabCounts[status] ?? null,
        }
      }
      const searching = searchTokens.length > 0
      const scoped = searching ? searchScopeCounts : activeScopeCounts
      const pick = (counts: { flagged: number; mine: number } | null) =>
        counts === null
          ? null
          : view.id === "builtin:attention"
          ? counts.flagged
          : counts.mine
      return {
        id: view.id,
        name: view.name,
        count: pick(scoped),
        totalCount: pick(activeScopeCounts),
      }
      // "My students · 0" is noise for staff who merely hold the mentor role
      // (Mayer): the pill renders only once the active data proves a non-empty
      // roster. Pure mentors still LAND on mine via the role default — the
      // pill appearing with the data is the same beat the counts arrive on.
    })
    .filter((pill) => pill.id !== "builtin:mine" || (pill.totalCount ?? 0) > 0)

  const visibleData = useMemo(() => {
    if (!isDataReady) return []
    let rows = data
    if (myStudentsOnly) {
      rows = rows.filter((row) => row.staff?.mentor?.userId === user?.clientId)
    }
    if (flaggedOnly && selectedProgramStatus === "active") {
      rows = rows.filter((row) => {
        const signals = getAttentionSignals(row)
        return signalFilter
          ? signals.some((signal) => signal.key === signalFilter)
          : signals.length > 0
      })
    }
    if (searchTokens.length > 0) {
      rows = rows.filter(rowMatchesSearch)
    }
    return rows
  }, [
    isDataReady,
    data,
    myStudentsOnly,
    flaggedOnly,
    signalFilter,
    selectedProgramStatus,
    searchTokens,
    rowMatchesSearch,
    user,
  ])

  const onManageProgramsClicked = (studentId: string, studentName: string) => {
    setManageProgramsFor({ id: studentId, name: studentName })
  }

  // ---- the chip strip: the selected view's definition, in one grammar ----
  // The Status chip is the scope every other predicate cuts inside — always
  // first, never removable. It opens a scope menu on custom views and on
  // unsaved chip sets; on built-ins the scope IS the pill, so the chip is
  // locked.
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(
    null,
  )
  const statusEditable = !isBuiltInViewId(activeViewId)
  const changeStatus = (status: ProgramStatus) => {
    if (status !== "active") {
      // Attention predicates are defined on running programs only — a
      // Completed set carrying a dead "Needs attention" chip would lie.
      setFlaggedOnly(false)
      setSignalFilter(null)
    }
    setSelectedProgramStatus(status)
    setStatusMenuAnchor(null)
  }

  const stripChips = useMemo<StripChip[]>(() => {
    const chips: StripChip[] = [
      {
        key: "status",
        label: `Status: ${VIEW_TITLES[selectedProgramStatus]}`,
        onClick: statusEditable
          ? (event) => setStatusMenuAnchor(event.currentTarget)
          : undefined,
      },
    ]
    if (myStudentsOnly) {
      chips.push({
        key: "mine",
        label: "Mentor is me",
        onRemove: () => setMyStudentsOnly(false),
      })
    }
    if (flaggedOnly && selectedProgramStatus === "active") {
      chips.push({
        key: "flagged",
        label: "Needs attention",
        // Dropping the attention predicate drops the signal cut with it — a
        // lone signal with no attention scope would be dead state.
        onRemove: () => {
          setFlaggedOnly(false)
          setSignalFilter(null)
        },
      })
      const signal = SIGNAL_PREDICATES.find(
        (candidate) => candidate.key === signalFilter,
      )
      if (signal) {
        chips.push({
          key: "signal",
          label: `Signal: ${signal.chipLabel}`,
          onRemove: () => setSignalFilter(null),
        })
      }
    }
    for (const [index, item] of activeFilterItems.entries()) {
      chips.push({
        key: String(item.id ?? `${item.field}-${index}`),
        label: filterChipLabel(item),
        onRemove: () => removeFilterItem(item),
      })
    }
    return chips
  }, [
    selectedProgramStatus,
    statusEditable,
    myStudentsOnly,
    flaggedOnly,
    signalFilter,
    activeFilterItems,
    filterChipLabel,
    removeFilterItem,
  ])

  const renderStripChip = (chip: StripChip) => (
    <Chip
      key={chip.key}
      size="small"
      variant="outlined"
      clickable={chip.onClick !== undefined}
      label={chip.label}
      onClick={chip.onClick}
      onDelete={chip.onRemove}
      deleteIcon={
        chip.onRemove ? (
          <SvgIcon
            component={appIcons.xClose}
            inheritViewBox
            aria-label={`Remove filter ${chip.label}`}
            sx={{
              width: "14px",
              height: "14px",
              fill: "none",
              stroke: "currentColor",
            }}
          />
        ) : undefined
      }
      sx={{
        maxWidth: "240px",
        flexShrink: 0,
        "&.Mui-focusVisible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
        },
      }}
    />
  )

  // Every pick writes into the same state the views save and restore: column
  // predicates go through the grid's filter model, attention and mentor-is-me
  // through the URL params the client-side filters already run on.
  const addPredicate = (predicate: PickedPredicate) => {
    if (predicate.kind === "mine") {
      setMyStudentsOnly(true)
      return
    }
    if (predicate.kind === "needsAttention") {
      setFlaggedOnly(true)
      setSignalFilter(null)
      return
    }
    if (predicate.kind === "signal") {
      setFlaggedOnly(true)
      setSignalFilter(predicate.signal)
      return
    }
    const api = gridApiRef.current
    if (typeof api?.setFilterModel !== "function") return
    const model = currentGridFilter ?? { items: [] }
    // One chip per field from the picker: re-picking a field replaces its
    // value — two ANDed values on one column would silently zero the grid.
    const items = [
      ...model.items.filter((item) => item.field !== predicate.field),
      {
        id: `picker-${predicate.field}-${Date.now()}`,
        field: predicate.field,
        operator: predicate.operator,
        value: predicate.value,
      },
    ]
    api.setFilterModel({ ...model, items })
  }

  return (
    <Box
      sx={{
        maxWidth: "100%",
        overflow: "hidden",
        paddingX: 3,
        // Tight top: the page title sits close to the top edge (Mayer — the old
        // 72px reserved a band of dead space above "Students").
        paddingTop: "28px",
        // Fill the height the tab container hands down (a flex column) rather
        // than sizing to content, so the grid below reaches the viewport bottom
        // with no dead band — the whole column flexes instead of relying on a
        // fixed viewport-minus-constant grid height (Mayer: stop leaving blank
        // space under fixed-height areas).
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ManageProgramsDialog
        open={manageProgramsFor !== null}
        studentId={manageProgramsFor?.id ?? null}
        studentName={manageProgramsFor?.name}
        onClose={() => setManageProgramsFor(null)}
      />
      {/* EVERY student-action popup, hosted once at the tab root — the same set
          the program page mounts. Without this the row menu could offer actions
          it had no popup to open. Each is redux-driven and renders null while
          idle, so mounting the whole set costs nothing. */}
      <StudentActionPopups />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          flex: "1 1 auto",
          minHeight: 0,
          gap: "8px",
          overflowX: "hidden",
        }}
      >
        <Typography variant="h5">Students</Typography>
        {/* Search leads its own full-width line — nothing shares its row, so
            it never fights the view controls for space and the eye lands on
            it first. Its height is fixed (a small field), so load never shifts
            the rows below. */}
        <TextField
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search students by name, email or class"
          size="small"
          sx={{ width: "100%" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AppIcon icon={appIcons.searchSm} />
              </InputAdornment>
            ),
          }}
        />
        {/* The search-verdict slot renders only WHILE searching — idle, it
            collapses so the views row sits right under the search (Mayer: no
            dead band above the tabs). Height stays fixed while present, so a
            verdict appearing or vanishing mid-search never nudges the grid. */}
        {searchQuery.trim() !== "" && (
          <Box
            sx={{
              height: "32px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              overflow: "hidden",
            }}
          >
            {searchMatchCounts &&
              searchMatchCounts[selectedProgramStatus] === 0 &&
              PROGRAM_STATUSES.some(
                (status) =>
                  status !== selectedProgramStatus &&
                  searchMatchCounts[status] > 0,
              ) && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    No matches on {VIEW_TITLES[selectedProgramStatus]} ·
                  </Typography>
                  {PROGRAM_STATUSES.filter(
                    (status) =>
                      status !== selectedProgramStatus &&
                      searchMatchCounts[status] > 0,
                  ).map((status) => (
                    <Button
                      key={status}
                      size="small"
                      onClick={() => selectView(STATUS_VIEW_IDS[status])}
                    >
                      {searchMatchCounts[status]} on {VIEW_TITLES[status]}
                    </Button>
                  ))}
                </>
              )}
            {searchMatchCounts &&
              searchMatchCounts[selectedProgramStatus] > 0 &&
              visibleData.length === 0 &&
              (myStudentsOnly || flaggedOnly) && (
                <Typography variant="body2" color="text.secondary">
                  {searchMatchCounts[selectedProgramStatus]} match
                  {searchMatchCounts[selectedProgramStatus] === 1
                    ? ""
                    : "es"}{" "}
                  on this status hidden by the current view's filters.
                </Typography>
              )}
          </Box>
        )}
        {/* The views row is the primary control: built-ins are the old status
            tabs (plus attention and my-students) as definitional pills with
            honest counts, custom views follow, then "+ New view". It sits in
            its own reserved 32px slot so pills appearing or vanishing never
            shift the strip or the grid below. */}
        <SavedViewsBar
          builtIns={builtInPills}
          customViews={views}
          activeViewId={activeViewId}
          isDrifted={isDrifted}
          onSelectView={selectView}
          onNewView={() => setViewDialog({ kind: "save", name: "" })}
          onRenameView={(viewId) => {
            const view = views.find((candidate) => candidate.id === viewId)
            if (view) {
              setViewDialog({ kind: "rename", viewId, name: view.name })
            }
          }}
          onDeleteView={deleteView}
        />
        {/* The chip strip, flush above the grid: [+ Filter] leads, every
            predicate of the selected view renders as one chip in one grammar,
            drift actions surface inline at the right when a saved view's
            definition differs, and Export is the lone far-right action. The
            leading control, drift actions and Export hold fixed slots — only
            the chip strip reflows — and the height is pinned so the data
            loader overflows into the gaps rather than nudging the grid. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            height: "32px",
          }}
        >
          <FilterFieldPicker
            programStatus={selectedProgramStatus}
            rows={isDataReady ? data : []}
            hasMentorRole={hasMentorRole}
            onPick={addPredicate}
          />
          <FilterChipStrip items={stripChips} renderChip={renderStripChip} />
          {/* The data loader sits between the growing chip strip and the
              right-side actions, so when it appears the strip yields exactly
              its width and nothing else moves. */}
          {isLoading && (
            <Box sx={{ flexShrink: 0, display: "inline-flex" }}>
              <LoadingComponent loadedComponents="data" />
            </Box>
          )}
          {activeSavedView !== undefined && isDrifted && (
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Button
                size="small"
                onClick={updateActiveView}
                sx={controlButtonSx}
              >
                Save
              </Button>
              <Button
                size="small"
                onClick={() => setViewDialog({ kind: "save", name: "" })}
                sx={controlButtonSx}
              >
                Save as new
              </Button>
              <Button
                size="small"
                onClick={resetActiveView}
                sx={controlButtonSx}
              >
                Reset
              </Button>
            </Box>
          )}
          <ExportControl gridApiRef={gridApiRef} />
        </Box>
        <StudentsTableGrid
          data={visibleData}
          loading={data === "idle" || data === "pending"}
          programStatus={selectedProgramStatus}
          isPureMentor={pureMentor}
          apiRef={gridApiRef}
          // The grid's own filter panel is retired on this page: predicates
          // come from the [+ Filter] pickers, so there is no page control to
          // anchor a panel onto.
          panelAnchorEl={null}
          gridFilterSeq={gridFilterSeq}
          consumePendingGridFilter={consumePendingGridFilter}
          onGridFilterChanged={setCurrentGridFilter}
          expandedStudentId={expandedStudentId}
          onExpandedStudentChanged={(studentId) => {
            const next = new URLSearchParams(searchParams)
            if (studentId) next.set("student", studentId)
            else next.delete("student")
            setSearchParams(next, { replace: true })
          }}
          onManageProgramsClicked={onManageProgramsClicked}
        />
      </Box>

      {/* The Status chip's scope menu (custom views and unsaved chip sets
          only — on built-ins the scope is the pill itself). */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={statusMenuAnchor !== null}
        onClose={() => setStatusMenuAnchor(null)}
      >
        {PROGRAM_STATUSES.map((status) => (
          <MenuItem
            key={status}
            selected={status === selectedProgramStatus}
            onClick={() => changeStatus(status)}
          >
            {VIEW_TITLES[status]}
          </MenuItem>
        ))}
      </Menu>

      <GenericDialog
        open={viewDialog !== null}
        size="xs"
        onClose={() => setViewDialog(null)}
        title={viewDialog?.kind === "rename" ? "Rename view" : "New view"}
        content={
          <TextField
            autoFocus
            margin="dense"
            label="View name"
            fullWidth
            value={viewDialog?.name ?? ""}
            error={nameTaken}
            helperText={nameTaken ? "View with this name already exists" : ""}
            FormHelperTextProps={{ sx: { textTransform: "none" } }}
            onChange={(event) =>
              setViewDialog((current) =>
                current ? { ...current, name: event.target.value } : current,
              )
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                submitViewDialog()
              }
            }}
          />
        }
        buttons={[
          {
            text: "Cancel",
            type: "secondary",
            onClick: () => setViewDialog(null),
          },
          {
            text: "Save",
            type: "primary",
            disabled: !canSubmitDialog,
            onClick: submitViewDialog,
          },
        ]}
      />

      <Snackbar
        open={deletedView !== null}
        autoHideDuration={5000}
        onClose={(event, reason) => {
          if (reason !== "clickaway") setDeletedView(null)
        }}
        message={deletedView ? `Deleted "${deletedView.view.name}"` : ""}
        action={
          <Button color="inherit" size="small" onClick={undoDeleteView}>
            Undo
          </Button>
        }
      />
    </Box>
  )
}

// The chips as a SINGLE-ROW bounded strip: it flex-grows to absorb the row's
// slack (pinning the right-side actions to the edge), and chips past the slot
// fold into an "N more" hover popover measured from a hidden clone — a pile
// of predicates never wraps the row or shoves the actions.
function FilterChipStrip<T>(props: {
  items: T[]
  renderChip: (item: T, index: number) => JSX.Element
}) {
  const { items, renderChip } = props
  const containerRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const [visibleCount, setVisibleCount] = useState(items.length)
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null)

  // How many chips fit in the slot, measured from a hidden clone at natural
  // widths so the math never guesses at label lengths. Recomputed when the
  // items change and on band resize.
  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return undefined
    const GAP = 8
    const compute = () => {
      const available = container.clientWidth
      const widths = Array.from(measure.children).map(
        (child) => (child as HTMLElement).offsetWidth,
      )
      // Clone order: [...chips, widest possible "N more"].
      const moreWidth = widths[widths.length - 1] ?? 0
      const chipWidths = widths.slice(0, -1)
      const total = chipWidths.reduce(
        (sum, width, index) => sum + (index > 0 ? GAP : 0) + width,
        0,
      )
      if (total <= available) {
        setVisibleCount(chipWidths.length)
        return
      }
      let used = 0
      let fit = 0
      for (const width of chipWidths) {
        const next = used + (fit > 0 ? GAP : 0) + width + GAP + moreWidth
        if (next > available) break
        used += (fit > 0 ? GAP : 0) + width
        fit += 1
      }
      setVisibleCount(fit)
    }
    compute()
    const observer = new ResizeObserver(compute)
    observer.observe(container)
    return () => observer.disconnect()
  }, [items])

  const visible = items.slice(0, visibleCount)
  const folded = items.slice(visibleCount)

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "8px",
        overflow: "hidden",
      }}
    >
      {/* Hidden clone row: every chip at natural width, for the fit math. */}
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          height: 0,
          overflow: "hidden",
          visibility: "hidden",
          pointerEvents: "none",
          display: "flex",
          gap: "8px",
          whiteSpace: "nowrap",
        }}
      >
        {items.map((item, index) => renderChip(item, index))}
        <Chip size="small" variant="outlined" label={`${items.length} more`} />
      </Box>
      {visible.map((item, index) => renderChip(item, index))}
      {folded.length > 0 && (
        <>
          <Chip
            size="small"
            variant="outlined"
            clickable
            label={`${folded.length} more`}
            onClick={(event) => setOverflowAnchor(event.currentTarget)}
            onMouseEnter={(event) => setOverflowAnchor(event.currentTarget)}
          />
          <Popover
            open={overflowAnchor !== null}
            anchorEl={overflowAnchor}
            onClose={() => setOverflowAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            disableRestoreFocus
            slotProps={{
              paper: {
                onMouseLeave: () => setOverflowAnchor(null),
                sx: {
                  p: "8px",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  maxWidth: "480px",
                },
              },
            }}
          >
            {folded.map((item, index) =>
              renderChip(item, index + visible.length),
            )}
          </Popover>
        </>
      )}
    </Box>
  )
}

const PROGRAM_STATUSES: ProgramStatus[] = ["active", "ended", "upcoming"]

// Built-in view names for the three status scopes — the same words label the
// pills, the Status chip and the cross-view search hints.
const VIEW_TITLES: Record<ProgramStatus, string> = {
  upcoming: "Upcoming",
  active: "Active",
  ended: "Completed",
}

// Operator words for chip labels: the grid's operator tokens read like code
// ("equals", "isAnyOf") — the chips speak the strip's one sentence grammar.
// Unmapped operators (numeric >, <, …) fall through as-is.
const FILTER_OPERATOR_LABELS: Record<string, string> = {
  is: "is",
  equals: "is",
  "=": "is",
  not: "is not",
  "!=": "is not",
  contains: "contains",
  doesNotContain: "does not contain",
  startsWith: "starts with",
  endsWith: "ends with",
  isEmpty: "is empty",
  isNotEmpty: "is not empty",
  isAnyOf: "is any of",
}

const BUILT_IN_STATUS: Record<string, ProgramStatus> = {
  "builtin:active": "active",
  "builtin:ended": "ended",
  "builtin:upcoming": "upcoming",
}

const STATUS_VIEW_IDS: Record<ProgramStatus, string> = {
  active: "builtin:active",
  ended: "builtin:ended",
  upcoming: "builtin:upcoming",
}
